import React, {
  useState,
  KeyboardEventHandler,
  useRef,
  ReactNode,
  ChangeEventHandler,
  MouseEventHandler,
  useCallback,
} from "react";
import { styled } from "../../stitches.config";
import { useClickAwayListener } from "../../hooks/useClickAwayListener";
import TextInput, { InputProps } from "./TextInput";
import Icon from "./Icon";
import { FormControl, Label, LabelWrapper, RequiredSign } from "./Label";
import { useArrowNav } from "../../hooks/useArrowNav";
import { IconName } from "@fortawesome/fontawesome-common-types";
import { scaleIn, scaleOut } from "./Animations";
import { useEscapePress } from "../../hooks/useEscapePress";

const Content = styled("div", {
  backgroundColor: "$white",
  position: "absolute",
  top: "100%",
  left: "0",
  transformOrigin: "top bottom",
  overflowX: "hidden",
  overflowY: "auto",
  marginTop: "$0_5",
  borderRadius: "$rounded",
  border: "solid 1px $primaryLight",
  boxShadow: "$md",
  width: "100%",
  zIndex: 2,
  maxHeight: "195px",
  display: "block",
  "&[data-state='open']": {
    animation: `${scaleIn} 300ms`,
  },
  "&[data-state='close']": {
    animation: `${scaleOut} 300ms`,
    transform: "scale(0)",
  },
  "&[data-state='initial']": {
    display: "none",
  },
});

const CloseBtn = styled("button", {
  backgroundColor: "$white",
  border: "none",
});

const List = styled("ul", {
  listStyle: "none",
  margin: 0,
  padding: 0,
});

const ListItem = styled("li", {
  width: "100%",
  "&[data-selected='true'] button": {
    backgroundColor: "$successLightest",
  },
});

const Btn = styled("button", {
  padding: "$2 $4",
  backgroundColor: "$white",
  border: "none",
  fontSize: "$base",
  width: "100%",
  textAlign: "left",
  display: "inline-flex",
  justifyContent: "space-between",
  alignItems: "center",
  "&:hover": {
    backgroundColor: "$accentLighter",
  },
  "&:focus": {
    backgroundColor: "$accentLighter",
    border: "none",
    outline: "none",
  },
});

const Placeholder = styled("li", {
  display: "inline-block",
  padding: "$2 $4",
  fontSize: "$base",
  width: "100%",
  textAlign: "left",
  color: "$primary",
});

export type AutocompleteProps<Option> = {
  options?: Option[];
  selected: Option | null;
  size?: InputProps["size"];
  icon?: IconName;
  loading?: boolean;
  readOnly?: boolean;
  label: string;
  extraLabel?: ReactNode;
  id: string;
  required?: boolean;
  placeholder?: string;
  onResetOptions?: () => void;
  getKey(option: Option): string;
  onSelect(option: Option | null): void;
  renderAddon?(option: Option | null): ReactNode;
  renderOption(opt: Option): ReactNode;
  noResultsRender?: ReactNode;
  addons?: ReactNode;
  onKeyUp?: KeyboardEventHandler<HTMLInputElement>;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  disabled?: boolean;
  inputValue: string;
  onInputChange: ChangeEventHandler<HTMLInputElement>;
};

export function Autocomplete<Option>(props: AutocompleteProps<Option>) {
  const {
    inputValue,
    onInputChange,
    loading = false,
    readOnly = false,
    options = [],
    selected = null,
    onResetOptions = () => {},
    label,
    id,
    required = false,
    placeholder,
    getKey,
    renderAddon,
    noResultsRender = "No results matched your search",
    onKeyUp,
    onKeyDown,
    renderOption,
    onSelect,
    icon,
    size = "normal",
    extraLabel = null,
    disabled = false,
  } = props;

  const rootRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [state, setState] = useState<"open" | "close" | "initial">("initial");

  const open = useCallback(() => {
    if (state !== "open") {
      setState("open");
    }
  }, [state]);

  const close = useCallback(() => {
    if (state === "open") {
      setState("close");
    }
  }, [state]);

  const clear: MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      e.stopPropagation();
      close();
      onSelect(null);
      onResetOptions();
    },
    [close, onSelect, onResetOptions]
  );

  const handleOnSelect = useCallback(
    (option: Option) => () => {
      onSelect(option);
      close();
    },
    [onSelect, close]
  );

  useEscapePress(
    useCallback(() => {
      close();
      inputRef.current?.blur();
    }, [close]),
    state === "open"
  );
  useClickAwayListener(rootRef, close);
  useArrowNav(listRef, inputRef, state === "open");

  return (
    <FormControl disabled={disabled} fill ref={rootRef}>
      <LabelWrapper>
        <Label htmlFor={id}>
          {label}
          {required && <RequiredSign>&nbsp;*</RequiredSign>}
        </Label>
        {extraLabel}
      </LabelWrapper>
      <TextInput
        ref={inputRef}
        placeholder={placeholder}
        css={{ width: "100%", gap: "$1" }}
        id={id}
        size={size}
        addonRight={
          <>
            {renderAddon && renderAddon(selected)}
            {selected && (
              <CloseBtn onClick={clear}>
                <Icon icon="times" />
              </CloseBtn>
            )}
            {options.length > 0 && !selected && <Icon icon="caret-down" />}
          </>
        }
        required={required}
        value={inputValue}
        icon={icon}
        onChange={onInputChange}
        onFocus={open}
        autoComplete="off"
        aria-owns={`${id}-results`}
        loading={loading}
        onKeyUp={onKeyUp}
        onKeyDown={onKeyDown}
        withoutWrapper
        readOnly={readOnly}
        disabled={disabled}
      />

      <Content data-state={state}>
        <List id={`${id}-results`} role="listbox" tabIndex={0} ref={listRef}>
          {loading ? (
            <Placeholder role="option" aria-selected="false">
              Loading...
            </Placeholder>
          ) : options?.length === 0 ? (
            <Placeholder role="option" aria-selected="false">
              {noResultsRender}
            </Placeholder>
          ) : (
            options.map((option) => {
              const isSelected =
                Boolean(selected) && getKey(selected!) === getKey(option);
              return (
                <ListItem
                  key={getKey(option)}
                  role="option"
                  data-selected={isSelected}
                  aria-selected={isSelected}
                >
                  <Btn onClick={handleOnSelect(option)}>{renderOption(option)}</Btn>
                </ListItem>
              );
            })
          )}
        </List>
      </Content>
    </FormControl>
  );
}
